import React, { useState, useEffect } from 'react';
import {
  Box,
  Grid,
  List,
  ListItem,
  ListItemIcon,
  CircularProgress,
  Button,
  Paper,
  Typography,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Checkbox,
  ListItemText,
  RootRef,
} from '@material-ui/core';
import { DragDropContext, Droppable, Draggable } from 'react-beautiful-dnd';
import { makeStyles } from '@material-ui/core/styles';
import { ArrowRightAlt, DoneAll } from '@material-ui/icons';

import useSnackbar from '../../hooks/useSnackbar';
import CourseWork from '../../models/CourseWork';
import utils from '../../libs/utils';

const useStyles = makeStyles((theme) => ({
  root: {
    maxWidth: '95%',
    borderRadius: 5,
    backgroundColor: 'white',
    paddingTop: 20,
    paddingBottom: 20,
    width: '95%',
  },
  paper: {
    marginTop: '2%',
    overflow: 'auto',
    backgroundColor: '#fafafa',
  },
  paperArrow: {
    marginTop: '2%',
    overflow: 'auto',
  },
  button: {
    margin: theme.spacing(0.5, 0),
    position: 'absolute',
    top: '50%',
    left: '50%',
    transform: 'translate(-50%, -50%)',
  },
  flexCenter: {
    height: '44px',
    display: 'flex',
    alignItems: 'center',
  },
  middleArrows: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    height: '60px',
  },
  middlePaper: {
    marginTop: '8%',
    paddingTop: 8,
  },
}));

interface CloneAssignmentSpecsModalProps {
  sourceCourseID: string;
  targetCourseID: string;
  visibility: boolean;
  onDone(): void;
  onClose(): void;
}

export const CloneAssignmentSpecsModal = ({
                                            sourceCourseID,
                                            targetCourseID,
                                            visibility,
                                            onDone,
                                            onClose,
                                          }: CloneAssignmentSpecsModalProps) => {
  const classes = useStyles();
  const [sourceAssignments, setSourceAssignments] = useState<CourseWork[]>([]);
  const [targetAssignments, setTargetAssignments] = useState<CourseWork[]>([]);
  const [isCloning, setIsCloning] = useState<boolean>(false);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [errorMessage, setErrorMessage] = useState<string>('');

  const snackbar = useSnackbar();

  // gets all assignments
  const fetchAllAssignments = async () => {
    try {
      setIsLoading(true);
      setErrorMessage('');
      setSourceAssignments([]);
      setTargetAssignments([]);

      const [sourceAssignmentsRes, targetAssignmentsRes] = await Promise.all([
        CourseWork.find({courseId: sourceCourseID}),
        CourseWork.find({courseId: targetCourseID})
      ]);
      setSourceAssignments(sourceAssignmentsRes.map(item => {
        item.checked = true;
        return item;
      }).filter(a => a.associatedWithDeveloper));
      setTargetAssignments(targetAssignmentsRes.map(item => {
        item.checked = true;
        return item;
      }).filter(a => a.associatedWithDeveloper));
    } catch (e) {
      utils.getRequestErrorMessage(e);
    } finally {
      setIsLoading(false);
    }
  };

  // starts cloning
  const executeClone = async () => {
    try {
      setIsCloning(true);

      const src =
        sourceAssignments.length > targetAssignments.length
          ? targetAssignments
          : sourceAssignments;
      const checkItems = src.filter(
        (_item, i) =>
          sourceAssignments[i].checked && targetAssignments[i].checked
      );

      await Promise.all(checkItems.map(async (_item, i) => {
        if (sourceAssignments[i].checked && targetAssignments[i].checked) {
          return await CourseWork.cloneSpecs(
            sourceAssignments[i].id,
            targetAssignments[i].id
          );
        }
        return;
      }));

      snackbar.showMessage('Cloned successfully!', 'success');
      onDone();
    } catch (e) {
      snackbar.showMessage(`Clone Error: ${e.message}`, 'error');
    } finally {
      setIsCloning(false);
    }
  };

  // handles drag end
  const onDragEnd = (result: any, items: CourseWork[], type: string) => {
    if (!result.destination) {
      return;
    }

    const list = utils.moveArrayItem(items, result.source.index, result.destination.index);

    if (type === 'source') {
      setSourceAssignments(list);
    } else {
      setTargetAssignments(list);
    }
  };

  // computes item style
  const getItemStyle = (isDragging: any, draggableStyle: any) => ({
    ...draggableStyle,

    ...(isDragging && {
      background: 'rgb(235,235,235)',
    }),
  });

  // handles checked change of assignment items
  const handleCheckChange = (checked: boolean, index: number, items: CourseWork[], type: string) => {
    (type === 'source' ? setSourceAssignments : setTargetAssignments)(items.map((item, i) => {
      if (index === i) item.checked = checked;
      return item;
    }));
  };

  // checks/unchecks all assignment items
  const checkAll = (checked: boolean, items: CourseWork[], type: string) => {
    (type === 'source' ? setSourceAssignments : setTargetAssignments)(items.map(item => {
      item.checked = checked;
      return item;
    }));
  };

  // checks if all items in a list is checked
  const checkAllValue = (items: CourseWork[]) => {
    return !items.some((item) => item.checked === false);
  };

  // render arrows
  const renderArrows = () => {
    const src =
      sourceAssignments.length > targetAssignments.length
        ? targetAssignments
        : sourceAssignments;
    return (
      <Paper
        className={classes.paperArrow}
        elevation={0}
        style={{ marginTop: '8%', paddingTop: 8 }}
      >
        <div className={classes.middleArrows}>
          {
            <DoneAll
              color={
                checkAllValue(sourceAssignments) &&
                checkAllValue(targetAssignments)
                  ? 'primary'
                  : 'disabled'
              }
            />
          }
        </div>
        {src.map((_item, i) => {
          const checked =
            sourceAssignments[i].checked && targetAssignments[i].checked;
          return (
            <div key={_item.id} className={classes.middleArrows}>
              <ArrowRightAlt color={checked ? 'primary' : 'disabled'} />
            </div>
          );
        })}
      </Paper>
    );
  };

  // render list
  const customList = (index: number, items: CourseWork[], type: 'source' | 'target') => (
    <Paper className={classes.paper} elevation={0}>
      {!!items.length && (
        <DragDropContext onDragEnd={(result) => onDragEnd(result, items, type)}>
          <Droppable droppableId="droppable">
            {(provided) => (
              <RootRef rootRef={provided.innerRef}>
                {!!items.length && (
                  <List dense component="div" role="list">
                    <ListItem>
                      <ListItemIcon className={classes.flexCenter}>
                        <>
                          {
                            <Checkbox
                              edge="start"
                              disabled={isCloning}
                              checked={checkAllValue(items)}
                              tabIndex={-1}
                              onChange={(e) =>
                                checkAll(e.target.checked, items, type)
                              }
                              disableRipple
                              color="primary"
                            />
                          }
                        </>
                      </ListItemIcon>
                      <ListItemText
                        primary="Select all"
                        className={classes.flexCenter}
                      />
                    </ListItem>
                    {items.map(
                      ({ checked, id, title }: CourseWork, index) => (
                        <Draggable
                          key={`drag${id}`}
                          draggableId={`drag${id}`}
                          isDragDisabled={isCloning}
                          index={index}
                        >
                          {(provided, snapshot) => (
                            <ListItem
                              ref={provided.innerRef}
                              {...provided.draggableProps}
                              {...provided.dragHandleProps}
                              style={getItemStyle(
                                snapshot.isDragging,
                                provided.draggableProps.style
                              )}
                              key={id}
                              role="listitem"
                              button
                            >
                              <ListItemIcon className={classes.flexCenter}>
                                <>
                                  {
                                    <Checkbox
                                      edge="start"
                                      disabled={isCloning}
                                      checked={!!checked}
                                      tabIndex={-1}
                                      onChange={(e) => handleCheckChange(
                                        !checked,
                                        index,
                                        items,
                                        type
                                      )}
                                      disableRipple
                                      inputProps={{ 'aria-labelledby': id }}
                                      color="primary"
                                    />
                                  }
                                </>
                              </ListItemIcon>
                              <ListItemText
                                id={id}
                                primary={`${title} (${id})`}
                                className={classes.flexCenter}
                              />
                            </ListItem>
                          )}
                        </Draggable>
                      )
                    )}
                    <ListItem />
                  </List>
                )}
              </RootRef>
            )}
          </Droppable>
        </DragDropContext>
      )}
    </Paper>
  );

  // close modal
  const closeModal = async () => {
    if (!isCloning) await onClose();
  };

  useEffect(() => {
    if (!!sourceCourseID && !!targetCourseID) {
      (async () => await fetchAllAssignments())();
    }
  }, [sourceCourseID, targetCourseID]);

  return (
    <>
      {snackbar.renderSnackbar()}
      <Dialog
        open={visibility}
        onClose={closeModal}
        fullWidth={true}
        maxWidth="xl"
        scroll="paper"
        aria-labelledby="clone-assignment-specs-modal-title"
        aria-describedby="clone-assignment-specs-modal-description">
        <DialogTitle id="scroll-dialog-title">
          Cloning assignment from Course ID ({sourceCourseID}) to Course ID (
          {targetCourseID})
        </DialogTitle>
        <DialogContent dividers={true}>
          {(() => {
            if (isLoading)
              return (
                <Box p={3}>
                  <Grid container
                        spacing={2}
                        justify="center"
                        alignItems="center">
                    <CircularProgress color="inherit" />
                  </Grid>
                </Box>
              );

            if (errorMessage)
              return (
                <Grid
                  container
                  spacing={2}
                  direction="column"
                  justify="center"
                  alignItems="center"
                  className="h-100">
                  <Typography variant="h6">Something went wrong</Typography>
                  <Typography>{errorMessage}</Typography>
                  <Button
                    variant="contained"
                    color="primary"
                    onClick={fetchAllAssignments}>
                    Retry
                  </Button>
                </Grid>
              );

            return (
              <>
                <div style={{ padding: 20 }}>
                  <Typography variant="subtitle2" gutterBottom>
                    Drag and Drop the list to match and clone from the source to the
                    target assignment. unchecked assignment will not be included to
                    the cloning process.
                  </Typography>
                </div>
                <Grid item>
                  <Grid container justify="center">
                    <Grid item xs={5}>
                      {customList(0, sourceAssignments, 'source')}
                    </Grid>
                    <Grid item xs={1}>
                      {renderArrows()}
                    </Grid>
                    <Grid item xs={5}>
                      {customList(1, targetAssignments, 'target')}
                    </Grid>
                  </Grid>
                </Grid>
              </>
            );
          })()}
        </DialogContent>
        <DialogActions>
          <Button
            startIcon={isCloning ? <CircularProgress color="inherit" size={20} /> : undefined}
            disabled={isCloning || isLoading || !!errorMessage}
            className="float-right"
            variant="contained"
            color="primary"
            onClick={executeClone}>
            Clone
          </Button>
          <Button
            disabled={isCloning}
            className="float-right mr-3"
            variant="outlined"
            color="primary"
            onClick={closeModal}>
            Close
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};
